import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useProtocolProgress } from '@/hooks/useProtocolProgress';
import { useNavigate } from 'react-router-dom';
import { 
  Target, 
  Trophy, 
  Flame, 
  Calendar, 
  ChevronRight, 
  Loader2,
  Clock,
  CheckCircle2,
  Sparkles
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function ProtocolTracker() {
  const { activeProtocols, isLoading, protocolAchievements } = useProtocolProgress();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const inProgressProtocols = activeProtocols.filter(p => !p.isComplete);
  const completedProtocols = activeProtocols.filter(p => p.isComplete);

  return (
    <div className="space-y-6">
      {/* Achievements Summary */}
      {protocolAchievements && protocolAchievements.earned.length > 0 && (
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              Your Protocol Achievements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {protocolAchievements.earned.map((achievement) => (
                <Badge 
                  key={achievement.id} 
                  variant="outline" 
                  className="px-3 py-1.5 text-sm bg-background/50"
                >
                  <span className="mr-1.5">{achievement.icon}</span>
                  {achievement.name}
                  <span className="ml-1.5 text-primary">+{achievement.points}XP</span>
                </Badge>
              ))}
            </div>
            {protocolAchievements.available.length > 0 && (
              <p className="text-xs text-muted-foreground mt-3">
                {protocolAchievements.available.length} more achievements to unlock
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* In Progress Protocols */}
      {inProgressProtocols.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Active Protocols
            </CardTitle>
            <CardDescription>
              Continue your health journey
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {inProgressProtocols.map((item) => {
              const progressPercent = (item.completedDays / item.protocol.duration_days) * 100;
              const needsAttention = item.lastCompletedDate && 
                new Date().getTime() - new Date(item.lastCompletedDate).getTime() > 48 * 60 * 60 * 1000;

              return (
                <div 
                  key={item.protocol.id} 
                  className={`p-4 rounded-lg border ${needsAttention ? 'border-amber-500/50 bg-amber-500/5' : 'bg-muted/30'} cursor-pointer hover:shadow-md transition-all`}
                  onClick={() => navigate(`/longevity/protocol/${item.protocol.id}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{item.protocol.name}</h4>
                        {needsAttention && (
                          <Badge variant="outline" className="text-amber-500 border-amber-500">
                            <Clock className="w-3 h-3 mr-1" />
                            Resume
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Day {item.currentDay} of {item.protocol.duration_days}
                      </p>
                    </div>
                    {item.streak > 1 && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Flame className="w-3 h-3 text-orange-500" />
                        {item.streak} day streak
                      </Badge>
                    )}
                  </div>

                  <Progress value={progressPercent} className="h-2 mb-3" />

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Started {item.startDate ? formatDistanceToNow(new Date(item.startDate), { addSuffix: true }) : 'recently'}
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 gap-1">
                      Continue <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Completed Protocols */}
      {completedProtocols.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Completed Protocols
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {completedProtocols.map((item) => (
              <div 
                key={item.protocol.id} 
                className="p-4 rounded-lg bg-green-500/5 border border-green-500/20 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{item.protocol.name}</h4>
                    <p className="text-xs text-muted-foreground">
                      Completed {item.protocol.duration_days} days
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-green-600">
                    <Sparkles className="w-3 h-3 mr-1" />
                    -{item.protocol.expected_il_delta_min} to -{item.protocol.expected_il_delta_max} IL
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {activeProtocols.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Active Protocols</h3>
            <p className="text-muted-foreground mb-4">
              Start a protocol to improve your longevity score
            </p>
            <Button onClick={() => navigate('/longevity')}>
              Browse Protocols
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
